import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";
import { useIgnoreStore } from "stores/IgnoreStore";

import { notifyError, notifySuccess } from "utility/notify";
import { FOTS } from "utility/typescript";

import IgnoreTypePicker, { IgnoreTypeData } from "./Rules/IgnoreTypePicker";
import SelectedFindings from "./SelectedFindings";

// -----------------------------------------------------------------------------

const IgnoreDialog = memo(function IgnoreDialog({
  finding,
  onClose,
}: IgnoreDialogProps): JSX.Element {
  const [, ignoreActions] = useIgnoreStore();
  const [, findingActions] = useFindingStore();

  const [ignoreTypeData, setIgnoreTypeData] = useState(initialIgnoreTypeData);
  const [ignoreReason, setIgnoreReason] = useState("");
  const [loading, setLoading] = useState(false);

  const onIgnore = () => {
    setLoading(true);

    setIgnoreTypeData({
      ...ignoreTypeData,
      ruleTypeError: false,
      regexError: false,
    });

    const { ignoreType, ruleType, assetRegex, ruleTypeError, regexError } =
      ignoreTypeData;

    if (ignoreType === "rule") {
      if (ruleType === "") {
        setIgnoreTypeData({ ...ignoreTypeData, ruleTypeError: true });
      }
      if (ruleType === "regex" && assetRegex === "") {
        setIgnoreTypeData({ ...ignoreTypeData, regexError: true });
      }
      if (ruleTypeError || regexError) {
        setLoading(false);
        return;
      }
    }

    const data = {
      uuid: finding.uuid,
      ignoreType,
      ruleType,
      ignoreReason,
      assetRegex,
      onSuccess,
      onError,
    };

    ignoreActions.ignoreFinding(data);
  };

  const onSuccess = () => {
    findingActions.resetDeeplinkFindings();
    if (ignoreTypeData.ignoreType === "instance") {
      notifySuccess("Ignored 1 finding");
    } else {
      notifySuccess("Created ignore rule");
    }
    setLoading(false);
    onClose();
  };

  const onError = () => {
    if (ignoreTypeData.ignoreType === "instance") {
      notifyError("Something went wrong ignoring the finding");
    } else {
      notifyError("Something went wrong creating the ignore rule");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Ignore Finding</DialogTitle>
      <DialogContent>
        <Typography variant="body1">1 selected finding:</Typography>
        <SelectedFindings findings={[finding]} />

        <IgnoreTypePicker
          data={ignoreTypeData}
          setData={setIgnoreTypeData}
          finding={finding}
        />

        <Typography sx={{ mt: 3, mb: 2 }}>Reason: (optional)</Typography>
        <TextField
          id="ignore-reason-text-field"
          placeholder={`Describe why you are ${
            ignoreTypeData.ignoreType === "instance"
              ? "ignoring this finding"
              : "creating this rule"
          }`}
          variant="outlined"
          multiline
          maxRows={4}
          fullWidth
          value={ignoreReason}
          onChange={(e: FOTS) => setIgnoreReason(e?.target?.value)}
        />
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton loading={loading} variant="contained" onClick={onIgnore}>
          Ignore
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default IgnoreDialog;

// -----------------------------------------------------------------------------

interface IgnoreDialogProps {
  finding: Finding;
  onClose: () => void;
}

const initialIgnoreTypeData: IgnoreTypeData = {
  ignoreType: "instance",
  ruleType: "",
  assetRegex: "",
  ruleTypeError: false,
  regexError: false,
};
